import { type Dispatch, Fragment, type SetStateAction, useState } from 'react'
import { toast } from 'react-toastify'
import styled from 'styled-components'

import { getToken } from '@loadsmart/loadsmart-ui/dist/theming'
import { Button, Dialog, Icon, Link, Text, Layout } from '@loadsmart/miranda-react'
import { toCSSValue } from '@loadsmart/miranda-react/dist/tokens'

import { useUploadToOpenDockStorage } from '../../hooks/useUploadToOpenDockStorage'
import FileInput from './FileInput'

export const ALLOWED_FILE_SIZE = 52428800 // 50 MB
export const ALLOWED_FILE_TYPES = ['.pdf', '.doc', '.docx', 'txt', '.csv', '.xlx', '.xlsx', '.zip']

function fileFormatBytes(bytes: number, decimals = 2) {
  if (bytes === 0) {
    return '0 B'
  }

  const BYTES_PER_KILOBYTE = 1024
  const dm = Math.max(0, decimals)
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']

  const unit = Math.floor(Math.log(bytes) / Math.log(BYTES_PER_KILOBYTE))

  return `${parseFloat((bytes / Math.pow(BYTES_PER_KILOBYTE, unit)).toFixed(dm))} ${sizes[unit]}`
}

const StyledFile = styled(FileInput)<{
  error: boolean
}>`
  background: transparent;
  border: ${({ error }) =>
    error
      ? `2px dashed ${toCSSValue('color-danger-100')}`
      : `2px dashed ${toCSSValue('color-neutral-40')}`};
  font-weight: 400;
  height: 165px;
  width: 100%;
`

const FileInfoName = styled(Layout.Stack)`
  width: 70%;
`
const FileListWrapper = styled.div`
  margin-top: ${getToken('space-l')};
  text-align: left;
`

const FileInfos = styled(Layout.Group)`
  margin-top: ${getToken('space-m')};
`

function notSupportedFileExtension(fileName: string) {
  return ALLOWED_FILE_TYPES?.indexOf(`.${fileName?.split('.').at(-1)?.toLowerCase()}`) === -1
}

type UploadDocumentsDialogProps = {
  open: boolean
  onClose: () => void
  allowedFileCount: number
  attachedFilesLength: number
  onFilesUploaded: Dispatch<
    SetStateAction<
      {
        name: string
        storageUrl: string
        storageKey: string
      }[]
    >
  >
  shipmentUUID: string
}

export default function UploadDocumentsDialog({
  onClose,
  open,
  allowedFileCount,
  attachedFilesLength,
  onFilesUploaded,
  shipmentUUID,
}: Readonly<UploadDocumentsDialogProps>) {
  const [files, setFiles] = useState<File[]>([])
  const [isUploading, setIsUploading] = useState(false)

  const fileError = files?.some(
    file => file.size > ALLOWED_FILE_SIZE || notSupportedFileExtension(file.name)
  )

  const fileExceedLimitError =
    attachedFilesLength + files.length > allowedFileCount
      ? `File number exceeded: You can upload up to ${allowedFileCount} files`
      : undefined

  const disableDialogPrimaryAction = Boolean(
    fileError ||
      files.length === 0 ||
      fileExceedLimitError ||
      attachedFilesLength + files.length > allowedFileCount
  )

  function getCustomFileListBody(value: File[], removeFile: (index: number) => void) {
    return (
      <div>
        <Text color="color-text-error" variant="body-sm-bold" className="text-left">
          {fileExceedLimitError}
        </Text>
        <FileListWrapper>
          <Text as="span" color="color-text-primary" variant="heading-sm-bold">
            {value?.length} {value?.length <= 1 ? 'File' : 'Files'} Selected
          </Text>
          {Array.from(value).map((currentFile, index) => {
            let currentFileError = null

            if (currentFile.size > ALLOWED_FILE_SIZE) {
              currentFileError = 'File size exceeded'
            } else if (currentFile.name && notSupportedFileExtension(currentFile.name)) {
              currentFileError = 'File extension not supported'
            }

            return (
              <Fragment key={currentFile.name}>
                <FileInfos justify="flex-start" gap="spacing-4" align="center">
                  <FileInfoName gap="spacing-2">
                    <Text variant="body-md-bold" color="color-text-tertiary">
                      {currentFile.name}
                    </Text>
                  </FileInfoName>
                  <Layout.Stack gap="spacing-2">
                    <Text variant="body-md" color="color-text-tertiary" className="text-center">
                      {fileFormatBytes(currentFile.size)}
                    </Text>
                  </Layout.Stack>
                  <Button
                    variant="icon-secondary"
                    size="small"
                    onClick={removeFile(index) as undefined}
                    aria-label={`Remove ${currentFile.name}`}
                    style={{ marginLeft: 'auto' }}
                  >
                    <Icon name="close" size="20" color="color-text-primary" />
                  </Button>
                </FileInfos>
                {currentFileError && (
                  <Text color="color-text-error" variant="body-sm-bold">
                    {currentFileError}
                  </Text>
                )}
              </Fragment>
            )
          })}
        </FileListWrapper>
      </div>
    )
  }

  function getCustomUploadBody(
    isDragging: boolean,
    fileCount: number,
    _?: boolean, //isLoading: we are not going to use it becuase we are using in the button instead
    sizeLimitInMb?: number
  ) {
    const isDisabled = files.length >= fileCount
    if (isDragging) {
      return (
        <Text variant="body-md" color="color-text-tertiary">
          Release to upload the file
        </Text>
      )
    }
    return (
      <Layout.Stack gap="spacing-2" justify="center" align="center">
        <Icon name="arrow-upload" size="36px" color="color-neutral-40" />
        <Text
          style={{ display: 'block' }}
          variant="body-md"
          color={isDisabled ? 'color-text-disabled' : 'color-text-tertiary'}
        >
          Drag and drop your files here or{' '}
          <Link disabled={isDisabled} href="return: false;">
            Browse files
          </Link>
        </Text>
        {sizeLimitInMb && (
          <Text
            style={{ display: 'block' }}
            variant="body-sm"
            color={isDisabled ? 'color-text-disabled' : 'color-text-tertiary'}
          >
            You can attach up to {fileCount} files with size limit of {sizeLimitInMb}MB each.
          </Text>
        )}
      </Layout.Stack>
    )
  }

  const { call: fetchUploadToOpenDockStorage } = useUploadToOpenDockStorage()

  return (
    <Dialog open={open} size="medium" variant="neutral">
      <Dialog.Header>Upload your files</Dialog.Header>
      <Dialog.Body>
        <StyledFile
          aria-label="File"
          name="carrierDocumentFileInput"
          onChange={(value: File[]) => setFiles(value)}
          value={[...files]}
          accept={ALLOWED_FILE_TYPES}
          error={false}
          fileCount={allowedFileCount}
          sizeLimitInMb={50}
          getCustomUploadBody={getCustomUploadBody}
          getCustomFileListBody={getCustomFileListBody}
          fileListAtBottom
        />
      </Dialog.Body>
      <Dialog.Actions>
        <Dialog.ActionTertiary
          onClick={() => {
            setFiles([])
            onClose()
          }}
        >
          Cancel
        </Dialog.ActionTertiary>
        <Dialog.ActionPrimary
          disabled={disableDialogPrimaryAction}
          loading={isUploading}
          onClick={async () => {
            setIsUploading(true)

            try {
              const results = await Promise.all(
                files.map(file => fetchUploadToOpenDockStorage(shipmentUUID, file))
              )

              results.forEach((result, index) => {
                if (result) {
                  onFilesUploaded(prevState => [
                    ...prevState,
                    {
                      name: files[index].name,
                      storageUrl: result.url,
                      storageKey: result.key,
                    },
                  ])
                } else {
                  toast.error(`There was an error uploading the file: ${files[index].name}`)
                }
              })
            } catch {
              toast.error('There was an error uploading your files. Please try again.')
            } finally {
              setIsUploading(false)
              setFiles([])
              onClose()
            }
          }}
        >
          Upload files
        </Dialog.ActionPrimary>
      </Dialog.Actions>
    </Dialog>
  )
}
